import { localStorageGetItem } from 'src/utils/local-storage';
import { sessionStorageGetItem } from 'src/utils/session-storage';

import { TakeoverState, TAKEOVER_STORAGE_KEY } from 'src/services/system/system.types';

/**
 * Reading the backend's server-sent-event streams: AI sourcing (JT1-1747) and the streamed
 * candidate search (JT1-1802).
 *
 * `fetch` rather than `EventSource`, because EventSource cannot send the Authorization header and
 * cannot POST. Requests carry the same login and takeover headers as the app's API calls
 * (`services/axios.ts`).
 */

/** The login and takeover headers every API call sends. */
export function streamAuthHeaders(): Record<string, string> {
  const token = localStorageGetItem('access_token', undefined);
  const takeoverState = sessionStorageGetItem(TAKEOVER_STORAGE_KEY);
  const takeoverToken = takeoverState ? (JSON.parse(takeoverState) as TakeoverState).token : null;

  return {
    Accept: 'text/event-stream',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(takeoverToken ? { 'X-Takeover-Account': takeoverToken } : {}),
  };
}

/**
 * Splits server-sent-event text into the JSON payloads of its `data:` lines.
 *
 * Returns the complete events and whatever trails after the last blank line, which is the start of
 * an event still arriving and must be prepended to the next chunk. `onMalformedError` turns a
 * non-JSON `event: error` payload into an event; any other malformed payload is skipped.
 */
export function parseSseChunk<T>(
  buffer: string,
  onMalformedError?: (data: string) => T,
): { events: T[]; rest: string } {
  const normalised = buffer.replace(/\r\n/g, '\n');
  const blocks = normalised.split('\n\n');
  const rest = blocks.pop() ?? '';
  const events: T[] = [];

  blocks.forEach((block) => {
    const lines = block.split('\n');
    const name = lines.find((line) => line.startsWith('event:'))?.slice(6).trim();
    const data = lines
      .filter((line) => line.startsWith('data:'))
      .map((line) => line.slice(5).trimStart())
      .join('\n');
    if (!data) return;
    try {
      events.push(JSON.parse(data) as T);
    } catch {
      if (name === 'error' && onMalformedError) events.push(onMalformedError(data));
    }
  });

  return { events, rest };
}

/**
 * Reads `response` to its end, calling `onEvent` for every event as it arrives. Returns whether a
 * terminal event (per `isTerminal`) was seen, so the caller can treat a stream that closed early as
 * a failure rather than waiting forever.
 */
export async function readEventStream<T>(
  response: Response,
  onEvent: (event: T) => void,
  isTerminal: (event: T) => boolean,
  onMalformedError?: (data: string) => T,
): Promise<boolean> {
  const reader = response.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let finished = false;

  const deliver = (events: T[]) =>
    events.forEach((event) => {
      if (isTerminal(event)) finished = true;
      onEvent(event);
    });

  // eslint-disable-next-line no-constant-condition
  while (true) {
    // eslint-disable-next-line no-await-in-loop
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const { events, rest } = parseSseChunk(buffer, onMalformedError);
    buffer = rest;
    deliver(events);
  }

  deliver(parseSseChunk(`${buffer}\n\n`, onMalformedError).events);
  return finished;
}
